import { catalogProducts, type CatalogProduct } from "@/data/accessoryQuoteCatalog";
import type { AccessoryCatalogProduct, AccessoryCatalogVariant } from "@/data/accessoryCatalog.generated";
import { getImportedAccessoryReferencePrice } from "@/data/accessoryQuotePriceMap.generated";

export const ACCESSORY_CART_STORAGE_KEY = "partition-stalls-accessory-quote-cart-v1";
export const ACCESSORY_QUOTE_HANDOFF_STORAGE_KEY = "partition-stalls-accessory-quote-handoff-v1";
export const ACCESSORY_QUOTE_CART_UPDATED_EVENT = "partition-stalls:accessory-quote-cart-updated";
export const MAX_ACCESSORY_CART_LINES = 24;
export const MAX_ACCESSORY_CART_QUANTITY = 999;

export type AccessoryQuoteCartLine = {
  id: string;
  brand: "Bobrick" | "ASI" | "Bradley";
  model: string;
  description: string;
  price: number | null;
  image: string | null;
  category: string;
  variantLabel?: string | null;
  sku?: string | null;
  quantity: number;
};

export type AccessoryQuoteHandoff = {
  lines: AccessoryQuoteCartLine[];
  itemCount: number;
  referenceSubtotal: number | null;
  quantity: string;
  message: string;
};

const approvedCatalogById = new Map(catalogProducts.map((product) => [product.id, product]));

function clampQuantity(value: unknown): number {
  const quantity = typeof value === "number" ? value : Number(value);
  if (!Number.isInteger(quantity) || quantity < 1) return 0;
  return Math.min(quantity, MAX_ACCESSORY_CART_QUANTITY);
}

function toCartLine(product: CatalogProduct, quantity: number): AccessoryQuoteCartLine {
  return {
    id: product.id,
    brand: product.brand,
    model: product.model,
    description: product.description,
    price: product.price,
    image: product.image,
    category: product.category,
    quantity,
  };
}

function importedCartLineId(product: AccessoryCatalogProduct, variant: AccessoryCatalogVariant) {
  return `imported::${encodeURIComponent(product.id)}::${encodeURIComponent(variant.id)}`;
}

function toImportedCartLine(product: AccessoryCatalogProduct, variant: AccessoryCatalogVariant, quantity: number): AccessoryQuoteCartLine {
  const id = importedCartLineId(product, variant);
  const variantLabel = variant.title && !/^default title$/i.test(variant.title) ? variant.title : null;
  const identifier = variant.sku || product.model || product.sku || product.title;
  return {
    id,
    brand: product.brand,
    model: identifier,
    description: product.title,
    price: getImportedAccessoryReferencePrice(id),
    image: product.images[0]?.url ?? null,
    category: product.family,
    variantLabel,
    sku: variant.sku,
    quantity,
  };
}

function getImportedStoredCartLine(candidate: Record<string, unknown>, id: string, quantity: number): AccessoryQuoteCartLine | null {
  if (!id.startsWith("imported::")) return null;
  const brand = candidate.brand;
  const model = candidate.model;
  const description = candidate.description;
  const category = candidate.category;
  if ((brand !== "Bobrick" && brand !== "ASI" && brand !== "Bradley") || typeof model !== "string" || !model.trim() || typeof description !== "string" || typeof category !== "string") return null;
  return {
    id,
    brand,
    model: model.trim().slice(0, 220),
    description: description.trim().slice(0, 420),
    price: getImportedAccessoryReferencePrice(id),
    image: typeof candidate.image === "string" && candidate.image.startsWith("/manus-storage/") ? candidate.image : null,
    category: category.trim().slice(0, 160),
    variantLabel: typeof candidate.variantLabel === "string" ? candidate.variantLabel.trim().slice(0, 220) : null,
    sku: typeof candidate.sku === "string" ? candidate.sku.trim().slice(0, 220) : null,
    quantity,
  };
}

function getCanonicalCartLine(candidate: Record<string, unknown>, id: string, quantity: number): AccessoryQuoteCartLine | null {
  const approvedProduct = approvedCatalogById.get(id);
  if (approvedProduct) return toCartLine(approvedProduct, quantity);
  return getImportedStoredCartLine(candidate, id, quantity);
}

/**
 * Browser storage is treated as untrusted. Legacy product detail and every imported
 * approved reference price are refreshed from project-managed records before display
 * or handoff; imported project-priced models deliberately remain quote-only.
 */
export function normalizeAccessoryQuoteCart(value: unknown): AccessoryQuoteCartLine[] {
  if (!Array.isArray(value)) return [];

  const quantities = new Map<string, number>();
  for (const candidate of value) {
    if (!candidate || typeof candidate !== "object") continue;
    const id = "id" in candidate && typeof candidate.id === "string" ? candidate.id : "";
    const quantity = clampQuantity("quantity" in candidate ? candidate.quantity : 0);
    if (!getCanonicalCartLine(candidate, id, 1) || !quantity || quantities.has(id)) continue;
    if (quantities.size >= MAX_ACCESSORY_CART_LINES) break;
    quantities.set(id, quantity);
  }

  return Array.from(quantities, ([id, quantity]) => {
    const candidate = value.find((item) => item && typeof item === "object" && "id" in item && item.id === id);
    return candidate && typeof candidate === "object" ? getCanonicalCartLine(candidate, id, quantity) : null;
  }).filter((line): line is AccessoryQuoteCartLine => Boolean(line));
}

export function addAccessoryQuoteCartItem(lines: AccessoryQuoteCartLine[], product: CatalogProduct): AccessoryQuoteCartLine[] {
  const normalized = normalizeAccessoryQuoteCart(lines);
  const existing = normalized.find((line) => line.id === product.id);
  if (existing) {
    return normalized.map((line) => line.id === product.id
      ? { ...line, quantity: Math.min(line.quantity + 1, MAX_ACCESSORY_CART_QUANTITY) }
      : line);
  }
  if (normalized.length >= MAX_ACCESSORY_CART_LINES) return normalized;
  return [...normalized, toCartLine(product, 1)];
}

export function addImportedAccessoryQuoteCartItem(lines: AccessoryQuoteCartLine[], product: AccessoryCatalogProduct, variant: AccessoryCatalogVariant): AccessoryQuoteCartLine[] {
  const normalized = normalizeAccessoryQuoteCart(lines);
  const id = importedCartLineId(product, variant);
  const existing = normalized.find((line) => line.id === id);
  if (existing) {
    return normalized.map((line) => line.id === id
      ? { ...line, quantity: Math.min(line.quantity + 1, MAX_ACCESSORY_CART_QUANTITY) }
      : line);
  }
  if (normalized.length >= MAX_ACCESSORY_CART_LINES) return normalized;
  return [...normalized, toImportedCartLine(product, variant, 1)];
}

export function updateAccessoryQuoteCartQuantity(lines: AccessoryQuoteCartLine[], productId: string, quantity: number): AccessoryQuoteCartLine[] {
  const normalized = normalizeAccessoryQuoteCart(lines);
  if (!normalized.some((line) => line.id === productId) || quantity <= 0) return normalized.filter((line) => line.id !== productId);
  const nextQuantity = clampQuantity(quantity);
  return normalized.map((line) => line.id === productId ? { ...line, quantity: nextQuantity } : line);
}

export function accessoryCartItemCount(lines: AccessoryQuoteCartLine[]): number {
  return normalizeAccessoryQuoteCart(lines).reduce((total, line) => total + line.quantity, 0);
}

export function accessoryCartSubtotal(lines: AccessoryQuoteCartLine[]): number | null {
  const normalized = normalizeAccessoryQuoteCart(lines);
  if (normalized.some((line) => line.price === null)) return null;
  return normalized.reduce((total, line) => total + (line.price ?? 0) * line.quantity, 0);
}

export function formatAccessoryCurrency(value: number): string {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(value);
}

export function getAccessoryDisplayName(product: Pick<AccessoryQuoteCartLine, "brand" | "model">): string {
  const model = product.model.trim();
  return model.toLocaleLowerCase().startsWith(product.brand.toLocaleLowerCase()) ? model : `${product.brand} ${model}`;
}

export function loadAccessoryQuoteCart(): AccessoryQuoteCartLine[] {
  if (typeof window === "undefined") return [];
  try {
    return normalizeAccessoryQuoteCart(JSON.parse(window.localStorage.getItem(ACCESSORY_CART_STORAGE_KEY) ?? "[]"));
  } catch {
    return [];
  }
}

export function saveAccessoryQuoteCart(lines: AccessoryQuoteCartLine[]): void {
  if (typeof window === "undefined") return;
  const serialized = JSON.stringify(normalizeAccessoryQuoteCart(lines));
  const previousValue = window.localStorage.getItem(ACCESSORY_CART_STORAGE_KEY);
  if (previousValue === serialized || (previousValue === null && serialized === "[]")) return;
  window.localStorage.setItem(ACCESSORY_CART_STORAGE_KEY, serialized);
  window.dispatchEvent(new Event(ACCESSORY_QUOTE_CART_UPDATED_EVENT));
}

export function clearAccessoryQuoteCart(): void {
  if (typeof window === "undefined") return;
  saveAccessoryQuoteCart([]);
  window.sessionStorage.removeItem(ACCESSORY_QUOTE_HANDOFF_STORAGE_KEY);
}

export function buildAccessoryQuoteHandoff(lines: AccessoryQuoteCartLine[]): AccessoryQuoteHandoff | null {
  const normalized = normalizeAccessoryQuoteCart(lines);
  if (!normalized.length) return null;
  const itemCount = normalized.reduce((total, line) => total + line.quantity, 0);
  const referenceSubtotal = accessoryCartSubtotal(normalized);
  const lineList = normalized.map((line, index) => {
    const price = line.price === null
      ? "project pricing requested"
      : `reference price ${formatAccessoryCurrency(line.price)} each — line total ${formatAccessoryCurrency(line.price * line.quantity)}`;
    const configuration = line.variantLabel ? ` — configuration ${line.variantLabel}` : "";
    const sku = line.sku ? ` — SKU ${line.sku}` : "";
    return `${index + 1}. ${getAccessoryDisplayName(line)}${configuration}${sku} — Qty ${line.quantity} — ${price}`;
  });
  const message = [
    "Accessory quote list — some models have approved reference prices and others require project pricing. Please confirm the exact model/configuration, final project price, freight, tax, compatibility, availability, and lead time.",
    "",
    "Selected accessory models:",
    ...lineList,
    "",
    referenceSubtotal === null
      ? "Reference merchandise subtotal: pending because one or more selected models require project pricing."
      : `Reference merchandise subtotal: ${formatAccessoryCurrency(referenceSubtotal)}.`,
  ].join("\n");

  return {
    lines: normalized,
    itemCount,
    referenceSubtotal,
    quantity: `${itemCount} accessory item${itemCount === 1 ? "" : "s"} across ${normalized.length} model${normalized.length === 1 ? "" : "s"}`,
    message,
  };
}

export function saveAccessoryQuoteHandoff(handoff: AccessoryQuoteHandoff): void {
  if (typeof window === "undefined") return;
  window.sessionStorage.setItem(ACCESSORY_QUOTE_HANDOFF_STORAGE_KEY, JSON.stringify({ lines: handoff.lines }));
}

export function consumeAccessoryQuoteHandoff(): AccessoryQuoteHandoff | null {
  if (typeof window === "undefined") return null;
  try {
    const value = window.sessionStorage.getItem(ACCESSORY_QUOTE_HANDOFF_STORAGE_KEY);
    window.sessionStorage.removeItem(ACCESSORY_QUOTE_HANDOFF_STORAGE_KEY);
    if (!value) return null;
    const parsed = JSON.parse(value) as { lines?: unknown };
    return buildAccessoryQuoteHandoff(normalizeAccessoryQuoteCart(parsed.lines));
  } catch {
    window.sessionStorage.removeItem(ACCESSORY_QUOTE_HANDOFF_STORAGE_KEY);
    return null;
  }
}
